import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import parseJWT from '../../helpers/parseJWT';
import { isEmptyCustom } from '../../helpers/utils';
import { useAppSelector } from '../../store';
import { setUserData } from '../auth/authReducer';
import { sendNotification } from '../../components/notifications/notificationsReducer';

const useTokenValidator = (): { verifyToken: () => boolean } => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { id: userId, jwt: storedJwt } = useAppSelector(state => state.auth);
    const internalUserJwt = new URLSearchParams(location.search).get('jwt');

    // Drop the jwt from the address bar once it has been read. This runs in an
    // effect (not inside verifyToken, which is invoked during render) and uses
    // router navigation instead of window.history.replaceState directly: the
    // History API call bypasses React Router's own history object, which both
    // desyncs useLocation() from the real URL and clobbers the idx/key it keeps
    // in history.state for back/forward tracking (@remix-run/router calls this
    // out explicitly as a source of bugs). `replace: true` swaps the current
    // history entry so the jwt is never reachable via the Back button. This
    // fires after render has committed, so any setUserData dispatch from
    // verifyToken() below has already persisted the token to redux by the time
    // the URL is stripped - stripping first would leave a render with neither
    // the URL jwt nor the stored one, bouncing the user to logged-out.
    useEffect(() => {
        if (!internalUserJwt) {
            return;
        }

        const params = new URLSearchParams(location.search);
        params.delete('jwt');
        const search = params.toString();

        navigate(
            {
                pathname: location.pathname,
                search: search ? `?${search}` : '',
                hash: location.hash,
            },
            { replace: true },
        );
    }, [internalUserJwt]);

    const verifyToken = () => {
        const jwt = internalUserJwt || storedJwt;

        const parsedToken = parseJWT(jwt);
        const currentTimeSeconds = Math.floor(Date.now() / 1000);
        const isJwtExpired = currentTimeSeconds > parsedToken?.exp;

        if (!jwt || isJwtExpired) {
            // dispatch(logout());
            return false;
        } else {
            if (!isEmptyCustom(parsedToken) && !isEmptyCustom(parsedToken.user)) {
                if (!parsedToken.user.enabled) {
                    dispatch(
                        sendNotification({
                            text: 'Your account is locked. Please reach out to the Advertising Apps team.',
                            context: 'danger',
                            msToClose: 0,
                        }),
                    );

                    return false;
                }
            }
            // if fresh login (user isn't in store)
            if (!userId) {
                dispatch(
                    setUserData({
                        id: parsedToken.user.id,
                        email: parsedToken.user.email,
                        name: parsedToken.user.name,
                        isInternal: parsedToken.isInternal,
                        ...(parsedToken.user.roles ? { roles: parsedToken.user.roles } : {}),
                        jwt,
                    }),
                );
            }
        }

        return !isJwtExpired;
    };

    return { verifyToken };
};

export default useTokenValidator;
